import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, TrendingUp, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { preventWheelChange } from './constants';
import { CustomDatePicker } from '../ui/custom-date-picker';
import { getTodayString, dateToString } from '../../utils/dateUtils';

export function AddEmployeeDialog({ 
  open, 
  onOpenChange, 
  formData, 
  setFormData, 
  phoneError, 
  handlePhoneChange, 
  onSubmit 
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button 
          data-testid="add-employee-btn"
          className="h-11 px-3 bg-gradient-to-r from-slate-800 to-slate-900 hover:from-slate-700 hover:to-slate-800 text-white rounded-xl shadow-lg shadow-slate-900/30"
          title="Add Employee"
        >
          <Plus className="w-4 h-4" />
          <UserPlus className="w-4 h-4 ml-1" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Add New Employee
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Name *</Label>
            <Input
              data-testid="employee-name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Employee name"
              required
              className="h-11 rounded-xl"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Phone Number *</Label>
            <Input
              data-testid="employee-phone"
              value={formData.contact}
              onChange={(e) => handlePhoneChange(e.target.value, false)}
              placeholder="10 digit phone number"
              inputMode="numeric"
              pattern="[0-9]*"
              required
              className={`h-11 rounded-xl ${phoneError ? 'border-red-500' : ''}`}
            />
            {phoneError && <p className="text-red-500 text-xs">{phoneError}</p>}
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Monthly Salary (₹) *</Label>
            <Input
              data-testid="employee-salary"
              type="number"
              value={formData.monthly_salary}
              onChange={(e) => setFormData({ ...formData, monthly_salary: e.target.value })}
              onWheel={preventWheelChange}
              placeholder="0"
              required
              className="h-11 rounded-xl"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Start Date *</Label>
              <CustomDatePicker
                value={formData.start_date}
                onChange={(newStartDate) => {
                  if (formData.end_date && newStartDate > formData.end_date) {
                    toast.error('Start date cannot be after end date');
                    return;
                  }
                  setFormData({ ...formData, start_date: newStartDate });
                }}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">End Date</Label>
              <CustomDatePicker
                value={formData.end_date}
                onChange={(newEndDate) => {
                  if (formData.start_date && newEndDate < formData.start_date) {
                    toast.error('End date cannot be before start date');
                    return;
                  }
                  setFormData({ ...formData, end_date: newEndDate });
                }}
                min={formData.start_date || undefined}
                placeholder="Leave empty if active"
              />
              <p className="text-xs text-slate-400">Only for terminated employees</p>
            </div>
          </div>
          <Button data-testid="save-employee-btn" type="submit" className="w-full h-11 bg-gradient-to-r from-slate-800 to-slate-900 hover:from-slate-700 hover:to-slate-800 text-white rounded-xl">
            Add Employee
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function EditEmployeeDialog({ 
  open, 
  onOpenChange, 
  editData, 
  setEditData, 
  editPhoneError, 
  handlePhoneChange, 
  onSubmit 
}) {
  // Get first day of next month for minimum date
  const getNextMonthFirstDay = () => {
    const now = new Date();
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    return dateToString(nextMonth);
  };

  const handleSalaryIncrementChange = (value) => {
    setEditData({ 
      ...editData, 
      salary_increment: value,
      new_salary: value === 'No' ? '' : editData.new_salary,
      new_salary_start_date: value === 'No' ? '' : editData.new_salary_start_date
    });
  };

  const handleNewSalaryChange = (value) => {
    setEditData({ ...editData, new_salary: value });
  };

  const handleNewSalaryDateChange = (value) => {
    setEditData({ ...editData, new_salary_start_date: value });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Edit Employee
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Name *</Label>
            <Input
              value={editData.name}
              onChange={(e) => setEditData({ ...editData, name: e.target.value })}
              placeholder="Employee name"
              required
              className="h-11 rounded-xl"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Phone Number *</Label>
            <Input
              value={editData.contact}
              onChange={(e) => handlePhoneChange(e.target.value, true)}
              placeholder="10 digit phone number"
              inputMode="numeric"
              pattern="[0-9]*"
              required
              className={`h-11 rounded-xl ${editPhoneError ? 'border-red-500' : ''}`}
            />
            {editPhoneError && <p className="text-red-500 text-xs">{editPhoneError}</p>}
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Current Monthly Salary (₹) *</Label>
            <Input
              type="number"
              value={editData.monthly_salary}
              onChange={(e) => setEditData({ ...editData, monthly_salary: e.target.value })}
              onWheel={preventWheelChange}
              placeholder="0"
              required
              className="h-11 rounded-xl"
            />
          </div>
          
          {/* Salary Increment Section */}
          <div className="space-y-2 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-200">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-4 h-4 text-emerald-600" />
              <Label className="text-sm font-semibold text-emerald-800">Salary Increment</Label>
            </div>
            
            <div className="space-y-2">
              <Label className="text-xs text-emerald-700">Apply Increment?</Label>
              <Select 
                value={editData.salary_increment || 'No'} 
                onValueChange={handleSalaryIncrementChange}
              >
                <SelectTrigger className="h-10 rounded-lg bg-white">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="No">No</SelectItem>
                  <SelectItem value="Yes">Yes</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {editData.salary_increment === 'Yes' && (
              <div className="space-y-3 mt-3 pt-3 border-t border-emerald-200">
                <div className="space-y-2">
                  <Label className="text-xs text-emerald-700">New Salary (₹) *</Label>
                  <Input
                    type="number"
                    value={editData.new_salary || ''}
                    onChange={(e) => handleNewSalaryChange(e.target.value)}
                    onWheel={preventWheelChange}
                    placeholder={`Min: ₹${editData.monthly_salary || 0}`}
                    min={editData.monthly_salary}
                    className="h-10 rounded-lg bg-white"
                  />
                  <p className="text-xs text-emerald-600">Must be greater than current salary (₹{parseFloat(editData.monthly_salary || 0).toLocaleString('en-IN')})</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-emerald-700">Start Date of New Salary *</Label>
                  <Input
                    type="month"
                    value={editData.new_salary_start_date ? editData.new_salary_start_date.slice(0, 7) : ''}
                    onChange={(e) => handleNewSalaryDateChange(e.target.value + '-01')}
                    min={getNextMonthFirstDay().slice(0, 7)}
                    className="h-10 rounded-lg bg-white"
                  />
                  <p className="text-xs text-emerald-600">Must be a future month (from {new Date(getNextMonthFirstDay()).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })} onwards)</p>
                </div>
              </div>
            )}
            
            {/* Show pending increment if exists */}
            {editData.pending_increment && (
              <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-xs font-semibold text-amber-800">Pending Increment</p>
                <p className="text-sm text-amber-700 mt-1">
                  New Salary: ₹{parseFloat(editData.pending_increment.new_salary).toLocaleString('en-IN')} 
                  <span className="text-xs ml-2">
                    (from {new Date(editData.pending_increment.start_date).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })})
                  </span>
                </p>
              </div>
            )}
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Start Date</Label>
              <CustomDatePicker
                value={editData.start_date}
                onChange={(newStartDate) => {
                  if (editData.end_date && newStartDate > editData.end_date) {
                    toast.error('Start date cannot be after end date');
                    return;
                  }
                  setEditData({ ...editData, start_date: newStartDate });
                }}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">End Date</Label>
              <CustomDatePicker
                value={editData.end_date}
                onChange={(newEndDate) => {
                  if (editData.start_date && newEndDate < editData.start_date) {
                    toast.error('End date cannot be before start date');
                    return;
                  }
                  setEditData({ ...editData, end_date: newEndDate });
                }}
                min={editData.start_date || undefined}
              />
              <p className="text-xs text-slate-400">Set to terminate employee</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="flex-1 h-11 rounded-xl">
              Cancel
            </Button>
            <Button type="submit" className="flex-1 h-11 bg-gradient-to-r from-slate-800 to-slate-900 hover:from-slate-700 hover:to-slate-800 text-white rounded-xl">
              Save Changes
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
